import { useId, useLayoutEffect, useRef, useState } from 'react';
import { AlertCircle, Bug, MessageSquarePlus, Send } from 'lucide-react';
import { StaffMarkdown } from '../../../shared/ui/markdown/StaffMarkdown';
import { useLocation } from 'react-router-dom';

import { ROUTE_PATHS } from '../../../app/router/route-paths';
import { useAssistantChat } from '../model/assistant-chat-context';
import {
  AssistantProposalCard,
  ACTION_LABELS,
  type ErrorReportPayload
} from './AssistantProposalCard';
import { AssistantReadResultCard } from './AssistantReadResultCard';
import { QuickTimeSuggestions } from './QuickTimeSuggestions';
import { StaffContactDialog } from '../../../pages/contact/StaffContactDialog';
import type { AuthMeFeedbackPayload } from '../../../entities/me/me.api';
import { Button } from '../../../shared/ui/Button';
import { Spinner } from '../../../shared/ui/Spinner';
import styles from './assistant.module.css';

/**
 * Назначение: лента и композер чата ИИ-помощника (общие для плавающего окна и страницы).
 * Кто: администратор организации (assistant:use); СА — через платформенный контекст.
 * Вход: облако-кнопка «Помощник» в shell; раздел «Помощник»; deep link /assistant.
 */

const COMPOSER_MAX_HEIGHT_PX = 160;

const DEFAULT_SUGGESTIONS = [
  'Поставь групповую тренировку на понедельник 20:00',
  'Какие занятия завтра утром?',
  'Найди клиента по номеру телефона'
];

const SCHEDULE_SUGGESTIONS = [
  'Поставь групповую тренировку на понедельник 20:00',
  'Какие занятия завтра утром?',
  'Покажи свободные окна на этой неделе'
];

const BOOKINGS_SUGGESTIONS = [
  'Какие записи на сегодня?',
  'Запиши клиента на завтра',
  'Покажи отменённые записи за неделю'
];

const PAYMENTS_SUGGESTIONS = [
  'Сколько оплат пришло сегодня?',
  'Покажи неоплаченные записи',
  'Какие платежи ожидают подтверждения?'
];

function suggestionsForPath(pathname: string): string[] {
  if (pathname === ROUTE_PATHS.schedule || pathname.startsWith(`${ROUTE_PATHS.schedule}/`)) {
    return SCHEDULE_SUGGESTIONS;
  }
  if (
    pathname === ROUTE_PATHS.bookings ||
    pathname.startsWith(`${ROUTE_PATHS.bookings}/`) ||
    pathname === ROUTE_PATHS.clientsBookings ||
    pathname.startsWith(`${ROUTE_PATHS.clientsBookings}/`)
  ) {
    return BOOKINGS_SUGGESTIONS;
  }
  if (
    pathname === ROUTE_PATHS.payments ||
    pathname.startsWith(`${ROUTE_PATHS.payments}/`) ||
    pathname === ROUTE_PATHS.billing ||
    pathname.startsWith(`${ROUTE_PATHS.billing}/`)
  ) {
    return PAYMENTS_SUGGESTIONS;
  }
  return DEFAULT_SUGGESTIONS;
}

function humanizeReply(content: string): string {
  const trimmed = content.trim();
  if (trimmed.startsWith('{') && (trimmed.includes('"intent"') || trimmed.includes('"kind"'))) {
    return 'Не удалось показать ответ помощника. Уточните запрос или откройте нужный раздел.';
  }
  return content;
}

export function AssistantChatPanel({ showNewDialog = true }: { showNewDialog?: boolean }) {
  const chat = useAssistantChat();
  const location = useLocation();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const composingRef = useRef(false);
  const composerId = useId();
  const composerHintId = useId();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackInit, setFeedbackInit] = useState<Partial<AuthMeFeedbackPayload>>({
    recipient: 'developers',
    category: 'complaint'
  });

  const suggestions = suggestionsForPath(location.pathname);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [chat.input]);

  const handleReportError = (payload: ErrorReportPayload) => {
    const lines: string[] = ['### Ошибка в работе ИИ-помощника'];
    if (payload.planId) lines.push(`- **ID плана:** ${payload.planId}`);
    if (payload.stepId || payload.action) {
      const act = payload.action ? (ACTION_LABELS[payload.action] ?? payload.action) : '';
      lines.push(`- **Шаг:** ${payload.stepId ?? ''}${act ? ` (${act})` : ''}`);
    }
    if (payload.error) lines.push(`- **Ошибка:** ${payload.error}`);
    if (payload.details) lines.push(`- **Детали:** ${payload.details}`);
    lines.push('\n**Комментарий:**\n');

    setFeedbackInit({
      recipient: 'developers',
      category: 'complaint',
      body_md: lines.join('\n'),
      is_anonymous: false
    });
    setFeedbackOpen(true);
  };

  function applySuggestion(suggestion: string) {
    chat.prefillInput(suggestion);
    textareaRef.current?.focus();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {showNewDialog && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="secondary" size="sm" onClick={chat.newDialog}>
            <MessageSquarePlus size={16} /> Новый диалог
          </Button>
          <label
            className={`flex cursor-pointer items-center gap-1.5 text-xs ${styles.muted}`}
            title="После успешного выполнения помощник откроет расписание."
          >
            <input
              type="checkbox"
              checked={chat.followAgent}
              onChange={(e) => chat.setFollowAgent(e.target.checked)}
              data-testid="assistant-follow-agent"
            />
            Следовать за агентом
          </label>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto" data-testid="assistant-messages">
        {chat.loading ? (
          <Spinner label="Загрузка диалога…" />
        ) : chat.bubbles.length === 0 ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-3 py-6 text-center"
            data-testid="assistant-empty-state"
          >
            <div>
              <p className="text-sm font-medium">С чего начать</p>
              <p className={`mt-1 text-xs ${styles.muted}`}>
                Выберите подсказку — текст появится в поле ввода, отправите его сами.
              </p>
            </div>
            <div className={styles.suggestionList}>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className={styles.suggestionButton}
                  onClick={() => applySuggestion(suggestion)}
                  data-testid="assistant-suggestion"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          chat.bubbles.map((b, i) => (
            <div key={i} className={styles.bubbleWrap}>
              <div className={`${styles.bubble} ${b.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant}`}>
                {b.role === 'user' ? humanizeReply(b.content) : <StaffMarkdown content={humanizeReply(b.content)} />}
              </div>
              {b.role === 'assistant' && (
                <QuickTimeSuggestions
                  content={b.content}
                  onSelectTime={(timeStr) => {
                    chat.prefillInput(`на ${timeStr}`);
                    textareaRef.current?.focus();
                  }}
                />
              )}
              {b.bugReport ? (
                <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                  <Bug size={14} className="shrink-0" />
                  <span>
                    Создан багрепорт #{b.bugReport.id}: {b.bugReport.summary} (категория: {b.bugReport.category})
                  </span>
                </div>
              ) : null}
              {b.proposal ? (
                <AssistantProposalCard
                  proposal={b.proposal}
                  busy={chat.busyPlanId === b.proposal.plan_id}
                  onExecute={chat.executePlan}
                  onCancel={chat.cancelPlan}
                  onRevalidate={chat.revalidatePlan}
                  onReportError={handleReportError}
                />
              ) : null}
              {b.actionError ? (
                <div className="flex items-center justify-between gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400">
                  <span>Ошибка: {b.actionError}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      handleReportError({
                        planId: b.proposal?.plan_id,
                        stepId: b.failedStep,
                        error: b.actionError
                      })
                    }
                    data-testid="assistant-report-action-error"
                  >
                    Сообщить разработчикам
                  </Button>
                </div>
              ) : null}
              {b.readResults?.map((r, ri) => (
                <AssistantReadResultCard key={`${r.tool}-${ri}`} result={r} />
              ))}
            </div>
          ))
        )}
      </div>

      {chat.error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400">
          <span className="flex items-center gap-1.5 min-w-0">
            <AlertCircle size={16} className="shrink-0" />
            <span className="break-words">{chat.error}</span>
          </span>
          <div className="flex items-center gap-2 shrink-0">
            {chat.failedMessage ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => void chat.retryLastMessage()}
                disabled={chat.sending}
                data-testid="assistant-retry-send"
              >
                Повторить запрос
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleReportError({ error: chat.error ?? 'Сбой в чате помощника' })}
              data-testid="assistant-report-chat-error"
            >
              Сообщить разработчикам
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor={composerId} className="sr-only">
            Сообщение помощнику
          </label>
          <span id={composerHintId} className="sr-only">
            Enter отправляет сообщение, Shift+Enter добавляет новую строку.
          </span>
          <textarea
            id={composerId}
            ref={textareaRef}
            className={styles.composer}
            rows={1}
            placeholder="Напишите помощнику…"
            value={chat.input}
            maxLength={4000}
            aria-describedby={composerHintId}
            data-testid="assistant-input"
            onChange={(e) => chat.setInput(e.target.value)}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              composingRef.current = false;
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey) return;
              if (composingRef.current || e.nativeEvent.isComposing) return;
              e.preventDefault();
              if (!chat.sending && chat.input.trim() !== '') {
                chat.send();
              }
            }}
            disabled={chat.sending}
          />
        </div>
        <Button
          className="shrink-0"
          onClick={() => chat.send()}
          disabled={chat.sending || chat.input.trim() === ''}
          data-testid="assistant-send"
        >
          {chat.sending ? 'Отправка…' : <><Send size={16} /> Отправить</>}
        </Button>
      </div>

      <StaffContactDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        initialValues={feedbackInit}
        title="Сообщить об ошибке разработчикам"
        description="Отчёт об ошибке уходит напрямую разработчикам платформы DOMCRM для исправления бага."
      />
    </div>
  );
}
